import {
  AutoComplete,
  type AutoCompleteChangeEvent,
  type AutoCompleteCompleteEvent,
  type AutoCompleteSelectEvent,
} from 'primereact/autocomplete';
import { useState } from 'react';
import { type IDriver } from '../models/Driver';
import OpenAPI from '../endpoint/Openapi';
import { type paths } from '../endpoint/Schema';
import { useDemoMode } from '../contexts/DemoModeContext';
import { maskEmail, maskFullName } from '../utils/piiMasking';

function UserSearch(props: {
  tenantId: string | undefined;
  handleNewUserSelected: (driver: IDriver) => void;
  handleInputOnChange: (searchText: string) => void;
}) {
  const { isDemoModeActive } = useDemoMode();
  const [searchText, setSearchText] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [searchedUsers, setSearchedUsers] =
    useState<
      paths['/api/v2/admin/drivers/search']['get']['responses']['200']['content']['application/json']['page']
    >();

  async function searchUser(event: AutoCompleteCompleteEvent) {
    try {
      const query = event.query.trim();
      if (query.length < 3) {
        return;
      }

      setLoading(true);

      const res = await OpenAPI.GET('/api/v2/admin/drivers/search', {
        params: {
          query: {
            searchTerm: query,
            tenantId: props.tenantId,
            skip: 0,
            take: 10,
          },
        },
      });
      setSearchedUsers(res.data?.page ?? []);
    } catch {
      setSearchedUsers([]);
    }

    setLoading(false);
  }

  function handleSelectUser(event: AutoCompleteSelectEvent) {
    props.handleNewUserSelected(event.value);
    setSearchText(
      isDemoModeActive ? maskEmail(event.value.email) : event.value.email,
    );
  }

  function handleOnChange(event: AutoCompleteChangeEvent) {
    setSearchText(event.value);
    props.handleInputOnChange(event.value);
  }
  function autocompleteItemTemplate(item: IDriver) {
    if (loading) {
      return <></>;
    }
    const displayName = isDemoModeActive
      ? maskFullName(item.givenName, item.surname)
      : [item.givenName, item.surname].filter(Boolean).join(' ');
    const displayEmail = isDemoModeActive ? maskEmail(item.email) : item.email;
    return <div>{`${displayName}, ${displayEmail}`}</div>;
  }

  return props.tenantId != null ? (
    <AutoComplete
      value={searchText}
      suggestions={searchedUsers ?? []}
      completeMethod={searchUser}
      onSelect={handleSelectUser}
      onChange={handleOnChange}
      aria-label="User"
      placeholder="Search user by email address or name"
      className="w-1/3 mb-2"
      inputClassName="min-w-[300px] text-xl"
      itemTemplate={autocompleteItemTemplate}
    />
  ) : (
    <></>
  );
}

export default UserSearch;
